import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/admin/lib/api";
import type { PredicateNode } from "@/admin/components/condition-builder/types";

export type ActionKind =
  "command" | "workflow_transition" | "edit_fields" | "navigate";
export type ActionVariant = "default" | "destructive";

export interface EntityAction {
  uuid: string;
  entity_type: string;
  key: string;
  label: string;
  description: string;
  icon_name: string;
  variant: ActionVariant;
  action_kind: ActionKind;
  handler_key: string;
  handler_config: Record<string, unknown>;
  availability: PredicateNode | Record<string, never>;
  permission_key: string;
  capture_fields: string[];
  confirmation: {
    message?: string;
    require_reason?: boolean;
    /** A command collects its capture_fields in a form before running. */
    collect_fields?: boolean;
  };
  sort_order: number;
  is_active: boolean;
  available?: boolean;
  /**
   * The action is offered, but performing it would step outside the tenant's
   * own rules - an upgrade past what the contractor may sell, say. Never hides
   * the action: the agent is told why and can override deliberately.
   */
  /**
   * Tri-state. `null` means the handler has no notion of eligibility -
   * NOT that the record is cleared. See needsExceptionCapture.
   */
  requires_override?: boolean | null;
  /** Plain-English reason, written for the person about to decide. */
  override_reason?: string;
  /** ``false`` when the refusal is a lock no exception may lift. */
  override_allowed?: boolean;
  /** The exception reason that fits the rule that refused, if it names one. */
  suggested_exception_key?: string;
  /**
   * ``true`` when this user may not apply the exception the action needs but
   * may send the action to an approver instead (WPS-180).
   */
  request_allowed?: boolean;
  /** A request for this action on this record that is waiting for a decision. */
  pending_request?: {
    uuid: string;
    action_label?: string;
    requested_by: string;
    requested_at: string;
  } | null;
}

export interface EntityActionHandler {
  key: string;
  config_schema: Record<string, unknown>;
}

export interface EntityActionLogEntry {
  uuid: string;
  entity_action: string | null;
  action_key: string;
  entity_type: string;
  resource_uuid: string;
  executed_by: string | null;
  captured_values: Record<string, unknown>;
  reason: string;
  result_status: "success" | "error";
  result_payload: Record<string, unknown>;
  created_at: string;
}

const ENTITY_ACTIONS_KEY = (entityUuid: string) =>
  ["entity-actions", entityUuid] as const;

const ENTITY_ACTIONS_EVALUATE_KEY = (
  entityUuid: string,
  resourceUuid: string,
) => ["entity-actions-evaluate", entityUuid, resourceUuid] as const;

export function useEntityActions(entityUuid: string | undefined) {
  return useQuery<EntityAction[]>({
    queryKey: ENTITY_ACTIONS_KEY(entityUuid ?? ""),
    queryFn: () =>
      api
        .get<EntityAction[]>(`/entities/admin/types/${entityUuid}/actions/`)
        .then((r) => r.data),
    enabled: !!entityUuid,
  });
}

export interface CaptureFieldAttribute {
  code_name: string;
  display_name: string;
  field_type: string;
  required: boolean;
  hint: string;
  description: string;
  configuration_data: Record<string, unknown>;
}

export interface EvaluatedEntityActionsResult {
  actions: EntityAction[];
  resource_values: Record<string, unknown>;
  capture_field_attributes?: CaptureFieldAttribute[];
}

/**
 * ``holdingUuid`` names the registration the actions are opened from, so a
 * customer edit prefills and corrects that registration's copy of the address.
 */
export function useEvaluatedEntityActions(
  entityUuid: string | undefined,
  resourceUuid: string | undefined,
  holdingUuid?: string,
) {
  return useQuery<EvaluatedEntityActionsResult>({
    queryKey: [
      ...ENTITY_ACTIONS_EVALUATE_KEY(entityUuid ?? "", resourceUuid ?? ""),
      holdingUuid ?? "",
    ],
    queryFn: () =>
      api
        .get<EvaluatedEntityActionsResult>(
          `/entities/admin/types/${entityUuid}/actions/evaluate/`,
          {
            params: {
              resource_uuid: resourceUuid,
              ...(holdingUuid ? { holding_uuid: holdingUuid } : {}),
            },
          },
        )
        .then((r) => r.data),
    enabled: !!entityUuid && !!resourceUuid,
  });
}

export function useCreateEntityAction(entityUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<EntityAction>) =>
      api
        .post<EntityAction>(
          `/entities/admin/types/${entityUuid}/actions/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_ACTIONS_KEY(entityUuid ?? "") });
    },
  });
}

export function useUpdateEntityAction(entityUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actionUuid,
      ...data
    }: Partial<EntityAction> & { actionUuid: string }) =>
      api
        .patch<EntityAction>(
          `/entities/admin/types/${entityUuid}/actions/${actionUuid}/`,
          data,
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_ACTIONS_KEY(entityUuid ?? "") });
    },
  });
}

export function useDeleteEntityAction(entityUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionUuid: string) =>
      api.delete(`/entities/admin/types/${entityUuid}/actions/${actionUuid}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_ACTIONS_KEY(entityUuid ?? "") });
    },
  });
}

export function useReorderEntityActions(entityUuid: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: { uuid: string; sort_order: number }[]) =>
      api.patch(`/entities/admin/types/${entityUuid}/actions/reorder/`, items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ENTITY_ACTIONS_KEY(entityUuid ?? "") });
    },
  });
}

/** Send an action to an approver; nothing on the record changes until decided. */
export function useRequestEntityAction(
  entityUuid: string | undefined,
  resourceUuid: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actionKey,
      reason_codes,
      comment,
    }: {
      actionKey: string;
      reason_codes: string[];
      comment: string;
    }) =>
      api
        .post(
          `/entities/admin/types/${entityUuid}/actions/${actionKey}/request/`,
          { resource_uuid: resourceUuid, reason_codes, comment },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ENTITY_ACTIONS_EVALUATE_KEY(
          entityUuid ?? "",
          resourceUuid ?? "",
        ),
      });
      qc.invalidateQueries({ queryKey: ["workflows", "issues"] });
      qc.invalidateQueries({ queryKey: ["holding"] });
    },
  });
}

export function useExecuteEntityAction(
  entityUuid: string | undefined,
  resourceUuid: string | undefined,
  holdingUuid?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actionKey,
      captured_values,
      reason,
      exception_grant_id,
    }: {
      actionKey: string;
      captured_values?: Record<string, unknown>;
      reason?: string;
      exception_grant_id?: string | null;
    }) =>
      api
        .post(
          `/entities/admin/types/${entityUuid}/actions/${actionKey}/execute/`,
          {
            resource_uuid: resourceUuid,
            captured_values: captured_values ?? {},
            reason: reason ?? "",
            exception_grant_id: exception_grant_id ?? undefined,
            ...(holdingUuid
              ? { record_context: { holding_uuid: holdingUuid } }
              : {}),
          },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ENTITY_ACTIONS_EVALUATE_KEY(
          entityUuid ?? "",
          resourceUuid ?? "",
        ),
      });
      qc.invalidateQueries({ queryKey: ["holding"] });
      qc.invalidateQueries({ queryKey: ["holdings"] });
      qc.invalidateQueries({ queryKey: ["warranty"] });
      qc.invalidateQueries({ queryKey: ["warranties"] });
      qc.invalidateQueries({ queryKey: ["registration"] });
      qc.invalidateQueries({ queryKey: ["registrations"] });
      qc.invalidateQueries({ queryKey: ["product"] });
      qc.invalidateQueries({ queryKey: ["consumer"] });
      qc.invalidateQueries({ queryKey: ["customer-address-matches"] });
    },
  });
}

export function useEntityActionHandlers() {
  return useQuery<EntityActionHandler[]>({
    queryKey: ["entity-action-handlers"],
    queryFn: () =>
      api
        .get<EntityActionHandler[]>("/entities/admin/action-handlers/")
        .then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });
}
